/**
 * Code Analysis Utilities for Research Phase 2
 * Computes deltas and analyzes code changes
 */

/**
 * Compute code delta between two sketch states
 * @param {Object} beforeState - Sketch state before change (from store.getSketchState())
 * @param {Object} afterState - Sketch state after change
 * @returns {Object} Delta metrics
 */
export function computeCodeDelta(beforeState, afterState) {
  try {
    // Handle null/undefined states
    if (!beforeState || !afterState) {
      return {
        linesAdded: 0,
        linesRemoved: 0,
        filesModified: [],
        totalLinesBefore: 0,
        totalLinesAfter: 0,
        changePercentage: 0
      }
    }

    let linesAdded = 0
    let linesRemoved = 0
    const filesModified = new Set()
    let totalLinesBefore = 0
    let totalLinesAfter = 0

    // Get all file names from both states
    const beforeFiles = beforeState.files || []
    const afterFiles = afterState.files || []
    const allFileNames = new Set([
      ...beforeFiles.map(f => f.name),
      ...afterFiles.map(f => f.name)
    ])

    // Compare each file
    for (const fileName of allFileNames) {
      const beforeFile = beforeFiles.find(f => f.name === fileName)
      const afterFile = afterFiles.find(f => f.name === fileName)

      const beforeContent = beforeFile?.content || ''
      const afterContent = afterFile?.content || ''

      // Split into lines for comparison
      const beforeLines = beforeContent.split('\n')
      const afterLines = afterContent.split('\n')

      totalLinesBefore += beforeLines.length
      totalLinesAfter += afterLines.length

      // Simple line-based diff
      if (beforeContent !== afterContent) {
        filesModified.add(fileName)

        // Approximate delta calculation
        const beforeSet = new Set(beforeLines.filter(l => l.trim()))
        const afterSet = new Set(afterLines.filter(l => l.trim()))

        // Lines in after but not in before (added)
        for (const line of afterSet) {
          if (!beforeSet.has(line)) {
            linesAdded++
          }
        }

        // Lines in before but not in after (removed)
        for (const line of beforeSet) {
          if (!afterSet.has(line)) {
            linesRemoved++
          }
        }
      }
    }

    // Calculate change percentage
    const changePercentage = totalLinesBefore > 0
      ? Math.round(((linesAdded + linesRemoved) / totalLinesBefore) * 100)
      : 0

    return {
      linesAdded,
      linesRemoved,
      filesModified: Array.from(filesModified),
      totalLinesBefore,
      totalLinesAfter,
      changePercentage
    }
  } catch (error) {
    console.error('[CodeAnalysis] Delta computation error:', error)
    return {
      linesAdded: 0,
      linesRemoved: 0,
      filesModified: [],
      totalLinesBefore: 0,
      totalLinesAfter: 0,
      changePercentage: 0
    }
  }
}

/**
 * Classify prompt pattern
 * @param {string} promptText - User's prompt text
 * @returns {Object} Classification result
 */
export function classifyPrompt(promptText) {
  if (!promptText || typeof promptText !== 'string') {
    return {
      primaryType: 'unknown',
      confidence: 0,
      hasCodeSnippet: false,
      wordCount: 0,
      questionMarks: 0,
      imperativeVerbs: 0
    }
  }

  const text = promptText.trim().toLowerCase()
  const wordCount = text.split(/\s+/).length
  const questionMarks = (text.match(/\?/g) || []).length

  // Check for code snippets (backticks, brackets, semicolons)
  const hasCodeSnippet = /[`{};]/.test(promptText)

  // Question indicators
  const questionWords = ['what', 'how', 'why', 'when', 'where', 'which', 'can', 'could', 'should', 'would', 'is', 'are', 'does', 'do']
  const hasQuestionWord = questionWords.some(word => 
    text.startsWith(word + ' ') || text.includes(' ' + word + ' ')
  )
  const isQuestion = questionMarks > 0 || hasQuestionWord

  // Command indicators (imperative verbs)
  const imperativeVerbs = ['make', 'create', 'add', 'change', 'remove', 'delete', 'fix', 'update', 'move', 'draw', 'animate', 'set']
  const imperativeCount = imperativeVerbs.filter(verb => 
    text.startsWith(verb + ' ') || text.includes(' ' + verb + ' ')
  ).length

  // Description indicators
  const descriptionWords = ['i want', 'i need', 'i would like', 'let me', 'help me']
  const isDescription = descriptionWords.some(phrase => text.includes(phrase))

  // Example-based indicators
  const isExample = text.includes('like') || text.includes('similar to') || text.includes('example')

  // Determine primary type
  let primaryType = 'command' // default
  let confidence = 0.5

  if (isQuestion) {
    primaryType = 'question'
    confidence = questionMarks > 0 ? 0.9 : 0.7
  } else if (imperativeCount >= 2) {
    primaryType = 'command'
    confidence = 0.8
  } else if (isExample && hasCodeSnippet) {
    primaryType = 'example'
    confidence = 0.8
  } else if (isDescription) {
    primaryType = 'description'
    confidence = 0.7
  } else if (imperativeCount === 1) {
    primaryType = 'command'
    confidence = 0.7
  }

  return {
    primaryType,
    confidence,
    hasCodeSnippet,
    wordCount,
    questionMarks,
    imperativeVerbs: imperativeCount,
    isQuestion,
    isCommand: imperativeCount > 0,
    isDescription,
    isExample
  }
}

/**
 * Calculate code complexity score (simple heuristic)
 * @param {Object} sketchState - Sketch state
 * @returns {number} Complexity score (0-100)
 */
export function calculateComplexity(sketchState) {
  try {
    if (!sketchState?.files) return 0

    let totalLines = 0
    let functionCount = 0
    let loopCount = 0
    let conditionalCount = 0

    for (const file of sketchState.files) {
      if (file.type !== 'js') continue

      const content = file.content || ''
      const lines = content.split('\n')
      totalLines += lines.length

      // Count functions
      functionCount += (content.match(/function\s+\w+/g) || []).length
      functionCount += (content.match(/\w+\s*=\s*function/g) || []).length
      functionCount += (content.match(/\w+\s*=>\s*/g) || []).length

      // Count loops
      loopCount += (content.match(/\bfor\s*\(/g) || []).length
      loopCount += (content.match(/\bwhile\s*\(/g) || []).length

      // Count conditionals
      conditionalCount += (content.match(/\bif\s*\(/g) || []).length
      conditionalCount += (content.match(/\belse\b/g) || []).length
      conditionalCount += (content.match(/\bswitch\s*\(/g) || []).length
    }

    // Simple complexity formula
    const complexity = Math.min(100, 
      totalLines * 0.1 + 
      functionCount * 5 + 
      loopCount * 3 + 
      conditionalCount * 2
    )

    return Math.round(complexity)
  } catch (error) {
    console.error('[CodeAnalysis] Complexity calculation error:', error)
    return 0
  }
}
